import React from "react";
import { usePathname } from "next/navigation";
import { motion } from "framer-motion";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { HeroBg } from "@/components/bnp-illustrations";

interface HeroContent {
  paths: string[];
  title: React.ReactNode;
  subtitle: React.ReactNode;
}

export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.6 },
};

const HERO_CONTENT: HeroContent[] = [
  {
    paths: ["/"],
    title: (
      <div className="w-[100%]">
        <p> Nigeria&rsquo;s Real Estate Opportunities,</p>
        <span className="text-bnp-[#19545e] text-bnp-cyan">
          De-risked for You
        </span>
      </div>
    ),
    subtitle:
      "A great place to Buy, Sell, Rent and Access properties with ease",
  },
  {
    paths: ["/managed-developments"],
    title: "Premium Development Options",
    subtitle:
      "Handpicked and choice de-risked development opportunities best for you!",
  },
  {
    paths: ["/managed-properties"],
    title: "Managed Properties You Can Trust",
    subtitle:
      "Explore verified properties professionally managed — quality homes, seamless bookings, and reliable support.",
  },
  {
    paths: ["/properties"],
    title: "Discover Nigerian Properties",
    subtitle:
      "Find your dream home with exclusive benefits like prime location, modern amenities, and competitive prices.",
  },
  {
    paths: ["/realtors"],
    title: "Realtors",
    subtitle:
      "Connect with top-tier and verified realtors to access choice real-estate opportunities",
  },
  {
    paths: ["/developers"],
    title: "Developers",
    subtitle:
      "Connect with top-tier and verified Developers to access choice real-estate opportunities",
  },
  {
    paths: ["/weekly-deals"],
    title: "Deals",
    subtitle:
      "Don’t Miss Out on Unbeatable Offers—No Agency Fees, Just Amazing Deals!",
  },
  {
    paths: ["/contact"],
    title: "Contact us today",
    subtitle:
      "Have Questions or Need Assistance? We're Here to Help—Reach Out Today and Let's Make Your Property Goals a Reality!",
  },
  {
    paths: ["/request-property"],
    title: "Make a Property Request",
    subtitle:
      "Stop searching from scratch. Set your property preferences once and receive instant, real-time alerts the moment matching properties hit the market",
  },
];

const Hero = () => {
  const pathname = usePathname();
  const router = useRouter();

  const isHome = router.pathname === "/";

  const getContentForCurrentPath = (): HeroContent => {
    const content = HERO_CONTENT.find((item) =>
      item.paths.includes(pathname || "/"),
    );
    return content || HERO_CONTENT[0];
  };

  const currentContent = getContentForCurrentPath();

  return (
    <section
      className={`${
        !isHome
          ? "bg-[url('/assets/images/HeroSub.png')] bg-cover bg-no-repeat"
          : "bg-white"
      } w-full flex flex-col items-center font-sans text-white`}
    >
      <div
        className={`relative overflow-hidden bg-bnp-dark ${
          !isHome
            ? "container w-[100%] xl:w-[90%] lg:rounded-lg h-[400px] sm:h-[350px] lg:h-[350px]"
            : "w-full h-[500px] sm:h-[550px] lg:h-[482px]"
        }`}
      >
        <div
          className={`${
            isHome ? "block" : "hidden"
          } absolute inset-0 bg-center bg-cover bg-no-repeat brightness-50`}
          style={{
            backgroundImage: "url('/assets/images/hero-bg-2.png')",
          }}
        />

        <div
          className={`${
            isHome ? "hidden" : "flex justify-center items-center"
          } absolute bottom-0 w-full`}
        >
          <HeroBg />
        </div>

        <div className="relative h-full mt-14">
          <div className="container mx-auto px-4 h-full">
            <div className="flex flex-col items-center justify-center h-full gap-5 lg:gap-2 max-w-[1027px] mx-auto">
              {isHome && (
                <motion.div
                  className="text-center text-lg max-w-[625px]"
                  variants={fadeInUp}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {currentContent.subtitle}
                </motion.div>
              )}
              <motion.h1
                className="font-bold text-[28px] sm:text-[36px] md:text-[42px] lg:text-[58px] text-center whitespace-pre-line"
                variants={fadeInUp}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                {currentContent.title}
              </motion.h1>
              {!isHome && (
                <motion.div
                  className="text-center text-lg max-w-[625px]"
                  variants={fadeInUp}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {currentContent.subtitle}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
